import { ExportChatTable } from "./ExportChatTable";
import { useContext, useEffect, useState } from "react";
import Container from "@mui/material/Container";
import {
  Constants,
  OrganizationContext,
  useDebouncedAbortableCall,
} from "hhn-commons";
import ExportChatFilter from "./ExportChatFilter";
import {
  AbortContext,
  IExportChatData,
  IOrganizationUser,
} from "@home-health-notify/domain";
import { IExportChatQueryParams } from "@home-health-notify/models";
import dayjs from "dayjs";

const EXPORT_CHAT_LIMIT = 10;

// Rendered as a section inside the "More" workspace (no own chrome/close).
export function ExportChat() {
  const { selectedOrgUser } = useContext(OrganizationContext);

  const emptyParams = {
    limit: EXPORT_CHAT_LIMIT,
    pageNo: 0,
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    status: undefined,
  };

  const [params, setParams] = useState<IExportChatQueryParams>(emptyParams);

  const [rowsPerPage, setRowsPerPage] = useState<number>(EXPORT_CHAT_LIMIT);

  const [filter, setFilter] = useState({
    startDate: undefined,
    endDate: undefined,
    status: undefined,
  });

  const [exportChats, setExportChats] = useState<IExportChatData>({
    data: [],
    limit: 0,
    total: 0,
    page: 1,
  });

  const updateState = <T,>(
    setState: React.Dispatch<React.SetStateAction<T>>,
    updates: Partial<T>,
  ) => {
    setState(prevState => ({ ...prevState, ...updates }));
  };

  const onSetFilter = (key: string, value: any) => {
    updateState(setFilter, { [key]: value });
  };

  const fetchExportChatData = useDebouncedAbortableCall(
    async (
      abortContext: AbortContext,
      selectedOrgUser: IOrganizationUser,
      params: IExportChatQueryParams,
    ) => {
      const newParams = {
        ...params,
        pageNo: params.pageNo + 1,
      };
      await selectedOrgUser.fetchExportChatData(newParams, abortContext);
      setExportChats(selectedOrgUser.getExportChatData());
    },
    Constants.API_DEBOUNCE_DELAY_IN_MS,
  );

  useEffect(() => {
    (async () => {
      selectedOrgUser && (await fetchExportChatData(selectedOrgUser, params));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, selectedOrgUser]);

  const onFilterClick = () => {
    const hasFilterChanged =
      params.startDate !== filter.startDate ||
      params.endDate !== filter.endDate ||
      params.status !== filter.status;

    updateState(setParams, {
      startDate: filter.startDate,
      endDate: filter.endDate,
      status: filter.status,
      ...(hasFilterChanged && { pageNo: 0 }),
    });
  };

  const onResetClick = () => {
    const resetValues = {
      startDate: undefined,
      endDate: undefined,
      status: undefined,
    };

    setParams(currentParams => {
      const hasFiltersToReset =
        currentParams.startDate !== undefined ||
        currentParams.endDate !== undefined ||
        currentParams.status !== undefined;
      return {
        ...currentParams,
        ...resetValues,
        ...(hasFiltersToReset && { pageNo: 0 }),
      };
    });
    setFilter({ ...resetValues });
  };

  const onSetPage = (value: number) => {
    setParams(prev => ({ ...prev, pageNo: value }));
  };

  const onPageLimitChange = (pageLimit: number, pageNumber: number) => {
    updateState(setParams, {
      pageNo: pageNumber,
      limit: pageLimit,
    });
    setRowsPerPage(pageLimit);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSetSearch = (value: string) => {
    updateState(setParams, {
      pageNo: 0,
      search: value ? value : undefined,
    });
  };

  const formatDate = (value: dayjs.Dayjs, isEndOfDay = false) => {
    const hours = isEndOfDay ? 23 : 0;
    const minutes = isEndOfDay ? 59 : 0;
    const seconds = isEndOfDay ? 59 : 0;
    return new Date(
      value.year(),
      value.month(),
      value.date(),
      hours,
      minutes,
      seconds,
    ).getTime();
  };

  const onSetFromDate = (value: dayjs.Dayjs) => {
    const dateFormatted = formatDate(value, false);
    onSetFilter("startDate", dateFormatted);
  };

  const onSetToDate = (value: dayjs.Dayjs) => {
    const dateFormatted = formatDate(value, true);
    onSetFilter("endDate", dateFormatted);
  };

  const onSetStatusFilter = (value: string | undefined) => {
    onSetFilter("status", value);
  };

  return (
    <Container data-testid="export-chat">
      <ExportChatFilter
        onSetSearch={onSetSearch}
        fromDate={filter.startDate}
        onSetFromDate={onSetFromDate}
        toDate={filter.endDate}
        onSetToDate={onSetToDate}
        status={filter.status}
        onSetStatus={onSetStatusFilter}
        onFilterButtonClick={onFilterClick}
        onResetButtonClick={onResetClick}
      />
      <ExportChatTable
        page={exportChats.page - 1}
        onSetPage={onSetPage}
        rowsPerPage={rowsPerPage}
        onPageLimitChange={onPageLimitChange}
        exportChats={exportChats}
      />
    </Container>
  );
}
